import {
  type Product,
  getFirstAttributeValue,
  getProductAndSiblingsColors,
  type Price,
  type ProductImage,
  type Variant,
  type Value,
  type ProductSibling,
  type ProductColor,
} from '@scayle/storefront-nuxt'
import { type MaybeRefOrGetter, toRef, computed } from 'vue'
import useRouteHelpers from '../composables/useRouteHelpers'
import { getFrontImage, getHoverImage } from '../utils/image'
import {
  getLowestPriceBetweenVariants,
  getSiblingsColors,
} from '~/utils/product'
import { getProductDetailRoute } from '~/utils/route'
import type { RouteLocationRaw } from '#vue-router'

type UseProductBaseInfoReturn = {
  brand: ComputedRef<string>
  name: ComputedRef<string>
  description: ComputedRef<string>
  price: ComputedRef<Price | undefined>
  colors: ComputedRef<Value[]>
  color: ComputedRef<ProductColor>
  colorHex: ComputedRef<string>
  siblings: ComputedRef<ProductSibling[]>
  primaryImage: ComputedRef<ProductImage | undefined>
  hoverImage: ComputedRef<ProductImage | undefined>
  link: ComputedRef<RouteLocationRaw | undefined>
  variants: ComputedRef<Variant[]>
  hasOneVariantOnly: ComputedRef<boolean>
  isAvailable: ComputedRef<boolean | undefined>
}

/**
 * Composable for extracting and computing base product information.
 *
 * @param {MaybeRefOrGetter<Product | undefined | null>} productItem - The product object or a ref/getter to it.
 * @returns {UseProductBaseInfoReturn} An object containing computed product properties for use in components.
 *
 * @example
 * const {
 *   brand, name, price, primaryImage, link
 * } = useProductBaseInfo(product)
 */
export function useProductBaseInfo(
  productItem: MaybeRefOrGetter<Product | undefined | null>,
): UseProductBaseInfoReturn {
  const product = toRef(productItem)
  const { toLocalePath } = useRouteHelpers()

  const brand = computed(() => {
    return (
      getFirstAttributeValue(product.value?.attributes, 'brand')?.label ?? ''
    )
  })

  const name = computed(() => {
    return (
      getFirstAttributeValue(product.value?.attributes, 'name')?.label ?? ''
    )
  })

  const description = computed(() => {
    return (
      getFirstAttributeValue(product.value?.attributes, 'description')?.label ??
      ''
    )
  })

  const color = computed(
    () =>
      getFirstAttributeValue(
        product.value?.attributes,
        'color',
      ) as ProductColor,
  )

  const colorHex = computed(
    () =>
      getFirstAttributeValue(product.value?.attributes, 'colorHex')?.label ??
      '',
  )

  const price = computed(() => {
    if (!product.value) {
      return
    }
    return getLowestPriceBetweenVariants(product.value)
  })

  const colors = computed(() => {
    return product.value
      ? getProductAndSiblingsColors(product.value, 'colorRange')
      : []
  })

  const primaryImage = computed(() => {
    if (!product.value) {
      return
    }
    return getFrontImage(product.value.images)
  })
  const hoverImage = computed(() => {
    if (!product.value) {
      return
    }
    return getHoverImage(product.value.images)
  })

  const siblings = computed(() => {
    return product.value ? getSiblingsColors(product.value) : []
  })

  const link = computed(() => {
    if (!product.value) {
      return
    }
    return toLocalePath(getProductDetailRoute(product.value))
  })

  const variants = computed(() => product.value?.variants || [])
  const hasOneVariantOnly = computed(() => variants.value.length === 1)

  const isAvailable = computed(() => {
    return product.value?.isActive || !product.value?.isSoldOut
  })

  return {
    brand,
    name,
    description,
    price,
    colors,
    color,
    colorHex,
    primaryImage,
    hoverImage,
    siblings,
    link,
    variants,
    hasOneVariantOnly,
    isAvailable,
  }
}
